import { database, filesCloud } from "../firebase/config";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

export const songService = {
  _segmentsCollection(eventId) {
    if (!eventId) throw new Error("eventId obbligatorio");
    return collection(database, "events", eventId, "segments");
  },

  _legacySongsCollection(eventId) {
    if (!eventId) throw new Error("eventId obbligatorio");
    return collection(database, "events", eventId, "songs");
  },

  _segmentDoc(eventId, segmentKey) {
    if (!eventId) throw new Error("eventId obbligatorio");
    if (!segmentKey) throw new Error("segmentKey obbligatorio");
    return doc(database, "events", eventId, "segments", segmentKey);
  },

  _legacySongDoc(eventId, songKey) {
    if (!eventId) throw new Error("eventId obbligatorio");
    if (!songKey) throw new Error("songKey obbligatorio");
    return doc(database, "events", eventId, "songs", songKey);
  },

  async getSegment(eventId, segmentKey) {
    const ref = this._segmentDoc(eventId, segmentKey);
    const snap = await getDoc(ref);
    if (snap.exists()) return snap.data();
    const legacyRef = this._legacySongDoc(eventId, segmentKey);
    const legacySnap = await getDoc(legacyRef);
    return legacySnap.exists() ? legacySnap.data() : null;
  },

  async listSegments(eventId) {
    const q = query(this._segmentsCollection(eventId), orderBy("order", "asc"));
    const qs = await getDocs(q);
    const items = [];
    qs.forEach((d) => items.push(d.data()));
    if (items.length > 0) return items;
    const legacyQ = query(
      this._legacySongsCollection(eventId),
      orderBy("order", "asc"),
    );
    const legacyQs = await getDocs(legacyQ);
    const legacyItems = [];
    legacyQs.forEach((d) => legacyItems.push(d.data()));
    return legacyItems;
  },

  subscribeToSegments(eventId, callback, onError) {
    const q = query(this._segmentsCollection(eventId), orderBy("order", "asc"));
    const legacyQ = query(
      this._legacySongsCollection(eventId),
      orderBy("order", "asc"),
    );
    let legacyUnsub = null;

    const unsub = onSnapshot(
      q,
      (qs) => {
        const items = [];
        qs.forEach((d) => items.push(d.data()));
        if (items.length > 0) {
          if (legacyUnsub) {
            legacyUnsub();
            legacyUnsub = null;
          }
          callback(items);
          return;
        }

        if (!legacyUnsub) {
          legacyUnsub = onSnapshot(
            legacyQ,
            (lqs) => {
              const legacyItems = [];
              lqs.forEach((d) => legacyItems.push(d.data()));
              callback(legacyItems);
            },
            (e) => {
              if (onError) onError(e);
            },
          );
        }
      },
      (e) => {
        if (onError) onError(e);
      },
    );

    return () => {
      unsub();
      if (legacyUnsub) legacyUnsub();
    };
  },

  async upsertSegment(eventId, segment) {
    if (!segment || !segment.key) throw new Error("segment.key obbligatorio");
    const ref = this._segmentDoc(eventId, segment.key);
    const existing = await getDoc(ref);
    const now = new Date();
    const payload = {
      ...segment,
      key: segment.key,
      updatedAt: now,
      ...(existing.exists() ? {} : { createdAt: now }),
    };
    await setDoc(ref, payload, { merge: true });
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data() : null;
  },

  async updateSegment(eventId, segmentKey, fields) {
    const ref = this._segmentDoc(eventId, segmentKey);
    await updateDoc(ref, { ...fields, updatedAt: new Date() });
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data() : null;
  },

  _segmentImagePath(eventId, segmentKey, ext = "jpg") {
    const safeExt = String(ext || "jpg").replace(/[^a-zA-Z0-9]/g, "") || "jpg";
    return `events/${eventId}/segments/${segmentKey}.${safeExt}`;
  },

  async uploadSegmentImage(eventId, segmentKey, fileOrBlob) {
    if (!eventId) throw new Error("eventId obbligatorio");
    if (!segmentKey) throw new Error("segmentKey obbligatorio");
    if (!fileOrBlob) throw new Error("file obbligatorio");

    const rawName = typeof fileOrBlob?.name === "string" ? fileOrBlob.name : "";
    const rawExt = rawName.includes(".") ? rawName.split(".").pop() : "";
    const contentType = fileOrBlob?.type || undefined;
    const ext =
      rawExt ||
      (contentType && contentType.includes("/")
        ? contentType.split("/").pop()
        : "jpg");

    const imagePath = this._segmentImagePath(eventId, segmentKey, ext);
    const storageRef = ref(filesCloud, imagePath);
    await uploadBytes(
      storageRef,
      fileOrBlob,
      contentType ? { contentType } : {},
    );
    const imageUrl = await getDownloadURL(storageRef);
    return { imageUrl, imagePath };
  },

  getSong(eventId, songKey) {
    return this.getSegment(eventId, songKey);
  },
  listSongs(eventId) {
    return this.listSegments(eventId);
  },
  subscribeToSongs(eventId, callback, onError) {
    return this.subscribeToSegments(eventId, callback, onError);
  },
  upsertSong(eventId, song) {
    return this.upsertSegment(eventId, song);
  },
  updateSong(eventId, songKey, fields) {
    return this.updateSegment(eventId, songKey, fields);
  },
};

export const segmentService = {
  ...songService,
  getSong: songService.getSegment,
  listSongs: songService.listSegments,
  subscribeToSongs: songService.subscribeToSegments,
  upsertSong: songService.upsertSegment,
  updateSong: songService.updateSegment,
};
